import React, { useContext } from "react";
import { NavLink, useLocation } from "react-router-dom";
import BurgerMenu from "./BurgerMenu.js";
import Icon from "../svg/Icon.js";
import Logo from "../svg/Logo.js";
import { gaNavClick } from "../../helpers/googleAnalytics.js";
import { Content } from "../layout/CommonContentContextProvider.js";
import LangPicker from "./LangPicker.js";
import {
    followUsContainer,
    followUsLabel,
    langSelectLabel,
    langSelectLabelIcon,
    menuGridContainer,
    menuIconStyleVariants,
    menuItemStyleVariants,
    menuItemTitleStyleVariants,
    menuNavLink,
    slidingLangPicker,
    nav,
    pickLangArrowIcon,
    styledHomeLogo,
    homeNavLink,
    searchLink,
} from "./Menu.css.js";
import { MenuRecordEntry, menuConfig } from "./menu-config.js";
import SocialMediaLinks from "../common/SocialMediaLinks.js";
import { activeNavLink } from "./common.css.js";
import { matchPath } from "react-router";

interface Props {
    closeMenu: () => void;
    toggleMenu: () => void;
}

const makePath = (p: string): string => p && `/${p}`;

const Menu: React.FC<Props> = ({ closeMenu, toggleMenu }) => {
    const { langCode, languages, common } = useContext(Content);
    const { pathname } = useLocation();

    const entries = Object.entries(menuConfig) as MenuRecordEntry[];
    return (
        <nav id="main-menu" className={nav}>
            <ul className={menuGridContainer}>
                <BurgerMenu toggleMenu={toggleMenu} inputId="menu-state" />
                {entries.map(([key, { p, m }]) => (
                    <li key={key} className={menuItemStyleVariants[key]}>
                        {key === "search" ? (
                            <NavLink
                                className={searchLink}
                                to={`/${langCode}/search`}
                                onClick={() => {
                                    gaNavClick(key, "menu", langCode, pathname);
                                    closeMenu();
                                }}
                            >
                                <Icon k={key} />
                            </NavLink>
                        ) : key === "followUs" ? (
                            <div
                                className={followUsContainer}
                                id="follow-us-header-nav"
                            >
                                <label className={followUsLabel}>
                                    {common[key]}
                                </label>
                                <SocialMediaLinks position="menu" />
                            </div>
                        ) : key === "pickLang" ? (
                            <>
                                <label
                                    className={langSelectLabel}
                                    htmlFor="lang-picker-state"
                                >
                                    <span className={slidingLangPicker}>
                                        <Icon
                                            className={langSelectLabelIcon}
                                            k="pickLang"
                                        />
                                        <span
                                            className={
                                                menuItemTitleStyleVariants[key]
                                            }
                                        >
                                            {languages[langCode]}
                                        </span>
                                        <Icon
                                            className={pickLangArrowIcon}
                                            k="chevronL"
                                        />
                                    </span>
                                </label>
                                <LangPicker closeMenu={closeMenu} />
                            </>
                        ) : (
                            <NavLink
                                className={
                                    key === "home" ? homeNavLink : menuNavLink
                                }
                                to={`/${langCode}${makePath(p ?? key)}`}
                                onClick={() => {
                                    gaNavClick(key, "menu", langCode, pathname);
                                    closeMenu();
                                }}
                                activeClassName={activeNavLink}
                                isActive={(_, { pathname }) => {
                                    return !!matchPath(pathname, {
                                        path: `/:langCode/${p ?? m ?? key}`,
                                        exact: p === "", // home page case
                                    });
                                }}
                            >
                                {key === "home" ? (
                                    <Logo
                                        className={styledHomeLogo}
                                        showFrame={true}
                                    />
                                ) : (
                                    <Icon
                                        className={menuIconStyleVariants[key]}
                                        k={key}
                                    />
                                )}
                                <span
                                    className={menuItemTitleStyleVariants[key]}
                                >
                                    {common[key]}
                                </span>
                            </NavLink>
                        )}
                    </li>
                ))}
            </ul>
        </nav>
    );
};

export default Menu;
